import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getCurrentUser, isAllBranchesRole } from '../utils/auth';
import { useToast } from './ToastContext';

const RefreshIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

export default function ARAgingDetails({ companyId: propCompanyId }) {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);
  const { showToast } = useToast();

  // Debug logging
  useEffect(() => {
    console.log('ARAgingDetails - currentUser:', currentUser);
    console.log('ARAgingDetails - canSelectAllBranches:', canSelectAllBranches);
    console.log('ARAgingDetails - user role:', currentUser?.role);
  }, [currentUser, canSelectAllBranches]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState(propCompanyId || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Set default date to TODAY to show latest data from BC
  const [agingAsOfDate, setAgingAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [gotoPageInput, setGotoPageInput] = useState('');
  
  // Filter states
  const [salespersonCode, setSalespersonCode] = useState('');
  const [externalDocumentNo, setExternalDocumentNo] = useState('');
  const [customerName, setCustomerName] = useState('');
  
  // Company/Branch filter
  const ALL_BRANCHES_VALUE = '';
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [companies, setCompanies] = useState([]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      console.log('ARAgingDetails - Companies response:', response.data);
      setCompanies(response.data || []);
      
      // Fetch AR data once companies are loaded
      if (response.data && Array.isArray(response.data)) {
        fetchARData(1);
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchARData = async (page = 1) => {
    try {
      setIsLoading(true);
      let queryParams = {};
      const companyIdParam = getCompanyIdParam();
      
      // Only send company_id if it has a value
      if (companyIdParam && companyIdParam.trim() !== '') {
        queryParams.company_id = companyIdParam;
      }
      if (agingAsOfDate) queryParams.report_date = agingAsOfDate;
      
      // Add salesperson code filter
      if (salespersonCode && salespersonCode.trim() !== '') {
        queryParams.salesperson_code = salespersonCode;
      }
      
      // Add external document no filter
      if (externalDocumentNo && externalDocumentNo.trim() !== '') {
        queryParams.external_document_no = externalDocumentNo;
      }
      
      // Add customer name filter
      if (customerName && customerName.trim() !== '') {
        queryParams.customer_name = customerName;
      }
      
      // PAGINATION - 20 records per page
      queryParams.page = page;
      queryParams.limit = 20;
      
      console.log('Fetching AR Aging with params:', JSON.stringify(queryParams));
      const response = await api.get('/ar-aging-details', { params: queryParams });
      console.log('Fetched AR Aging Details response:', response.data);
      console.log('Report Date Used:', agingAsOfDate);
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalCount(response.data.pagination?.total || 0);
        setCurrentPage(page);
      } else {
        console.error('Unexpected response format:', response.data);
        setData([]);
        setTotalPages(1);
        setTotalCount(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Failed to fetch AR aging details:', JSON.stringify(error.response?.data || error.message));
      // Don't show alert for 403 errors (token expired) - let the interceptor handle redirect
      if (error.response?.status !== 403) {
        console.error('Error loading AR data:', error.response?.data || error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Set companyId initially based on prop
  useEffect(() => {
    if (propCompanyId) {
      setCompanyId(propCompanyId);
    } else if (companies.length > 0 && !companyId) {
      setCompanyId(''); // Set empty string to allow all branches
    }
  }, [propCompanyId, companies]);

  // Trigger data fetch when filters change
  useEffect(() => {
    if (companyId !== undefined || companies.length > 0) {
      fetchARData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agingAsOfDate, selectedBranch, salespersonCode, externalDocumentNo, customerName]);

  const handleApplyFilter = () => {
    fetchARData(1); // Reset to page 1
  };

  const handleRefresh = () => {
    // Reset to current date and reload
    setAgingAsOfDate(new Date().toISOString().split('T')[0]);
    fetchARData(1);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setAgingAsOfDate(new Date().toISOString().split('T')[0]);
    setSelectedBranch(companyId || ALL_BRANCHES_VALUE);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchARData(newPage);
    }
  };

  const handleGotoPage = () => {
    const page = parseInt(gotoPageInput);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchARData(page);
      setGotoPageInput('');
    }
  };

  const handleGotoPageKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleGotoPage();
    }
  };

  const handleDownload = () => {
    showToast('Excel download feature will be implemented soon', 'info', 3000);
  };

  const handleAddNew = () => {
    showToast('Add new feature will be implemented soon', 'info', 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return '0.00';
    
    // Always show with minus sign if negative, no parenthesis, no peso sign
    if (num < 0) {
      return `${num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    return `${Math.abs(num).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper to format numeric strings with commas for input fields
  const formatInputNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // Strip everything except digits and one decimal point
    const cleanValue = value.toString().replace(/,/g, '');
    const parts = cleanValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Helper to unformat comma-separated strings to pure numeric strings
  const unformatInputNumber = (value) => {
    if (!value) return '0';
    return value.toString().replace(/,/g, '');
  };

  const calculateDaysAging = (dueDate) => {
    if (!dueDate) return { days: 0, category: '-' };
    
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return { days: Math.abs(diffDays), category: 'Overdue' };
    } else if (diffDays <= 30) {
      return { days: diffDays, category: '1-30 Days' };
    } else if (diffDays <= 60) {
      return { days: diffDays, category: '31-60 Days' };
    } else if (diffDays <= 90) {
      return { days: diffDays, category: '61-90 Days' };
    } else if (diffDays <= 120) {
      return { days: diffDays, category: '91-120 Days' };
    } else {
      return { days: diffDays, category: 'After 120+ Days' };
    }
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    // Use the raw numeric value from the row, not "0" string
    const amountCollectedRaw = row.amount_collected || 0;
    const pdcRaw = row.pdc || 0;
    const discountRaw = row.discount_amount || 0;
    const otherDeductionsRaw = row.other_deductions || 0;
    
    // Format these as strings with commas for display
    const amountFormatted = isNaN(amountCollectedRaw) ? '0.00' : formatCurrency(amountCollectedRaw);
    const pdcFormatted = isNaN(pdcRaw) ? '0.00' : formatCurrency(pdcRaw);
    const discountFormatted = isNaN(discountRaw) ? '0.00' : formatCurrency(discountRaw);
    const otherDeductionsFormatted = isNaN(otherDeductionsRaw) ? '0.00' : formatCurrency(otherDeductionsRaw);
    
    setEditForm({
      amount_collected: amountFormatted,
      pdc: pdcFormatted,
      discount_amount: discountFormatted,
      other_deductions: otherDeductionsFormatted,
      remarks: row.remarks || '',
      commitment_date: row.commitment_date || '',
      is_cr: row.is_cr || 0,  // AR/CR flag: 0=AR, 1=CR
      balance_collectibles: row.balance_collectibles || '0',
      // Store both raw numeric values and formatted display values
      _raw_amount_collected: amountCollectedRaw,
      _raw_pdc: pdcRaw,
      _raw_discount: discountRaw,
      _raw_other_deductions: otherDeductionsRaw,
      _display_amount_collected: amountFormatted,
      _display_pdc: pdcFormatted,
      _display_discount: discountFormatted,
      _display_other_deductions: otherDeductionsFormatted
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingRow(null);
    setEditForm({});
    // Reset display values when closing modal
    setTimeout(() => {
      setEditForm({
        amount_collected: '0',
        pdc: '0',
        discount_amount: '0',
        other_deductions: '0',
        remarks: '',
        commitment_date: '',
        is_cr: 0,
        balance_collectibles: '0',
        _display_amount_collected: '',
        _display_pdc: '',
        _display_discount: '',
        _display_other_deductions: ''
      });
    }, 0);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmitEdit = async () => {
    setSaving(true);
    try {
      // Prepare data with numeric values - use _raw_* values (always kept in sync by onChange/onBlur)
      const submitData = {
        amount_collected: editForm._raw_amount_collected ?? 0,
        pdc: editForm._raw_pdc ?? 0,
        discount_amount: editForm._raw_discount ?? 0,
        other_deductions: editForm._raw_other_deductions ?? 0,
        remarks: editForm.remarks,
        commitment_date: editForm.commitment_date,
        is_cr: parseInt(editForm.is_cr || 0),
        balance_collectibles: parseFloat(editForm.balance_collectibles || '0')
      };
      
      console.log('Saving payment arrangement:', { id: editingRow?.invoice_id, submitData });
      
      await api.put(`/collection-entries/${editingRow.invoice_id}`, submitData);
      showToast('✓ Payment arrangement updated successfully!', 'success', 4000);
      closeEditModal();
      fetchARData(currentPage);
    } catch (error) {
      console.error('Failed to update payment arrangement:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update payment arrangement.';
      showToast(`✗ ${errorMsg}`, 'error', 8000);
    } finally {
      setSaving(false);
    }
  };

  // Calculate totals
  const totals = {
    originalAmount: data.reduce((sum, item) => sum + (parseFloat(item.original_amount) || 0), 0),
    balance: data.reduce((sum, item) => sum + (parseFloat(item.balance) || 0), 0),
    collected: data.reduce((sum, item) => sum + (parseFloat(item.amount_collected) || 0), 0),
    discount: data.reduce((sum, item) => sum + (parseFloat(item.discount_amount) || 0), 0),
    deductions: data.reduce((sum, item) => sum + (parseFloat(item.other_deductions) || 0), 0),
    aging_1_30: data.reduce((sum, item) => sum + (parseFloat(item.aging_1_30_days) || 0), 0),
    aging_31_60: data.reduce((sum, item) => sum + (parseFloat(item.aging_31_60_days) || 0), 0),
    aging_61_90: data.reduce((sum, item) => sum + (parseFloat(item.aging_61_90_days) || 0), 0),
    aging_91_120: data.reduce((sum, item) => sum + (parseFloat(item.aging_91_120_days) || 0), 0),
    aging_after_120: data.reduce((sum, item) => sum + (parseFloat(item.aging_after_120_days) || 0), 0)
  };

  // Aging calculations
  const getAgingBucket = (dueDate) => {
    if (!dueDate) return '-';
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Overdue';
    if (diffDays <= 30) return '1-30 Days';
    if (diffDays <= 60) return '31-60 Days';
    if (diffDays <= 90) return '61-90 Days';
    if (diffDays <= 120) return '91-120 Days';
    return 'After 120+ Days';
  };

  const thStyle = {
    padding: '16px',
    background: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'left'
  };

  const tdStyle = {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151'
  };

  return (
    <div className="aged-ar-container">
      <div className="page-header">
        <h1>AR Aging Details</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleApplyFilter}
            disabled={isLoading}
            style={{ backgroundColor: '#e63740', color: '#ffffff' }}
          >
            {isLoading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Summary Grid */}
      <div className="grand-total-card">
        {agingAsOfDate && (
          <div className="aged-as-of">
            Aged as of: {new Date(agingAsOfDate + 'T00:00:00').toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        )}

        <div className="grand-total-header">
          <h2>Aging Summary</h2>
          <div className="grand-total-actions">
            <input
              type="date"
              value={agingAsOfDate || ''}
              onChange={(e) => setAgingAsOfDate(e.target.value)}
              className="form-control"
              style={{ width: '160px' }}
            />
            <button
              onClick={handleApplyFilter}
              className="btn btn-sm btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              onClick={handleClearFilter}
              className="btn btn-sm btn-secondary"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="grand-total-grid">
          <div className="grand-total-item">
            <span className="label">Total Balance</span>
            <span className="value">{formatCurrency(totals.balance)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">1-30 Days</span>
            <span className="value" style={{ color: '#16a34a' }}>{formatCurrency(totals.aging_1_30)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">31-60 Days</span>
            <span className="value" style={{ color: '#f59e0b' }}>{formatCurrency(totals.aging_31_60)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">61-90 Days</span>
            <span className="value" style={{ color: '#f97316' }}>{formatCurrency(totals.aging_61_90)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">91-120 Days</span>
            <span className="value" style={{ color: '#ea580c' }}>{formatCurrency(totals.aging_91_120)}</span>
          </div>
          <div className="grand-total-item highlight">
            <span className="label">After 120+ Days</span>
            <span className="value" style={{ color: '#991b1b' }}>{formatCurrency(totals.aging_after_120)}</span>
          </div>
        </div>
      </div>

      {/* All Filters Section */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '15px', padding: '12px 16px', background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        {canSelectAllBranches && (
          <>
            <label style={{ fontWeight: 500, fontSize: '14px' }}>Branch:</label>
            <select
              value={selectedBranch}
              onChange={(e) => { setSelectedBranch(e.target.value); }}
              className="form-control"
              style={{ width: '200px' }}
            >
              <option value={ALL_BRANCHES_VALUE}>All Branches</option>
              {companies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {company.name}
                </option>
              ))}
            </select>
          </>
        )}
        
        <label style={{ fontWeight: 500, fontSize: '14px' }}>Salesperson:</label>
        <input
          type="text"
          value={salespersonCode}
          onChange={(e) => { setSalespersonCode(e.target.value); }}
          className="form-control"
          placeholder="Enter salesperson code"
          style={{ width: '180px' }}
        />
        
        <label style={{ fontWeight: 500, fontSize: '14px' }}>External Document No.:</label>
        <input
          type="text"
          value={externalDocumentNo}
          onChange={(e) => { setExternalDocumentNo(e.target.value); }}
          className="form-control"
          placeholder="Enter external document no."
          style={{ width: '200px' }}
        />
        
        <label style={{ fontWeight: 500, fontSize: '14px' }}>Customer Name/Code:</label>
        <input
          type="text"
          value={customerName}
          onChange={(e) => { setCustomerName(e.target.value); }}
          className="form-control"
          placeholder="Search customer..."
          style={{ width: '200px' }}
        />
      </div>

      {/* Table Container */}
      <div className="card ar-table-card aged-ar-card">
        {isLoading ? (
          <div className="loading">Loading data...</div>
        ) : data.length === 0 ? (
          <div className="empty-state">No AR aging details found for the selected criteria.</div>
        ) : (
          <>
            <table className="table ar-table table-compact">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Posting Date</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Document Type</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Document No</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer Code</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>External Document No.</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Due Date</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Terms</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Balance</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>1-30 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>31-60 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>61-90 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>91-120 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>After 120+ Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Collected</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Deductions</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Remarks</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Delivery Date</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff', width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.invoice_id || index}>
                    <td>{formatDate(item.posting_date)}</td>
                    <td>{item.document_type || '-'}</td>
                    <td><strong>{item.document_no || '-'}</strong></td>
                    <td>{item.customer_no}</td>
                    <td><strong>{item.customer_name}</strong></td>
                    <td>{item.salesperson_name || '-'}</td>
                    <td><strong>{item.external_document_no_display || item.external_document_no || '-'}</strong></td>
                    <td>{formatDate(item.due_date)}</td>
                    <td>{item.terms || 'Net 30'}</td>
                    <td className="text-right" style={{ color: '#dc2626' }}>{formatCurrency(item.balance)}</td>
                    <td className="text-right" style={{ color: '#16a34a' }}>{formatCurrency(item.aging_1_30_days || 0)}</td>
                    <td className="text-right" style={{ color: '#f59e0b' }}>{formatCurrency(item.aging_31_60_days || 0)}</td>
                    <td className="text-right" style={{ color: '#f97316' }}>{formatCurrency(item.aging_61_90_days || 0)}</td>
                    <td className="text-right" style={{ color: '#ea580c' }}>{formatCurrency(item.aging_91_120_days || 0)}</td>
                    <td className="text-right" style={{ color: '#991b1b' }}>{formatCurrency(item.aging_after_120_days || 0)}</td>
                    <td className="text-right" style={{ color: '#16a34a' }}>{formatCurrency(item.amount_collected)}</td>
                    <td className="text-right" style={{ color: '#ef4444' }}>{formatCurrency(parseFloat(item.discount_amount) + parseFloat(item.other_deductions))}</td>
                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.remarks || '-'}</td>
                    <td>{formatDate(item.deliver_date)}</td>
                    <td>
                      <button
                        onClick={() => openEditModal(item)}
                        className="btn btn-sm"
                        style={{ backgroundColor: '#f3f4f6', padding: '4px 8px', fontSize: '12px' }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="5"><strong>TOTAL:</strong></td>
                  <td className="text-right">{formatCurrency(totals.originalAmount)}</td>
                  <td className="text-right" style={{ color: '#16a34a' }}>{formatCurrency(totals.aging_1_30)}</td>
                  <td className="text-right" style={{ color: '#f59e0b' }}>{formatCurrency(totals.aging_31_60)}</td>
                  <td className="text-right" style={{ color: '#f97316' }}>{formatCurrency(totals.aging_61_90)}</td>
                  <td className="text-right" style={{ color: '#ea580c' }}>{formatCurrency(totals.aging_91_120)}</td>
                  <td className="text-right" style={{ color: '#991b1b' }}>{formatCurrency(totals.aging_after_120)}</td>
                  <td className="text-right" style={{ color: '#16a34a' }}>{formatCurrency(totals.collected)}</td>
                  <td className="text-right" style={{ color: '#ef4444' }}>{formatCurrency(totals.discount + totals.deductions)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            {/* Pagination Controls - 20 records per page */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', marginTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>
                Showing {totalCount === 0 ? 0 : (currentPage - 1) * 20 + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} invoices
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="btn btn-sm"
                  style={{ 
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-sm"
                  style={{ 
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{ color: '#374151', fontWeight: 500, fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="btn btn-sm"
                  style={{ 
                    opacity: currentPage >= totalPages ? 0.5 : 1,
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="btn btn-sm"
                  style={{ 
                    opacity: currentPage >= totalPages ? 0.5 : 1,
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Last
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={gotoPageInput}
                    onChange={(e) => setGotoPageInput(e.target.value)}
                    onKeyDown={handleGotoPageKeyDown}
                    placeholder="Page #"
                    style={{
                      width: '70px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleGotoPage}
                    className="btn btn-sm"
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    Go
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Payment Arrangement</h3>
              <button className="close-btn" onClick={closeEditModal}>✕ Close</button>
            </div>

            <div className="modal-body">
              <div className="customer-info" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e3f9' }}>
                <div style={{ fontSize: '13px', color: '#5a6c7d', marginBottom: '6px', fontWeight: '500' }}>Customer Name</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>{editingRow?.customer_name || ''}</div>
              </div>

              {/* Delivery Date */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Delivery Date</label>
                <input
                  type="date"
                  name="commitment_date"
                  value={editForm.commitment_date}
                  onChange={handleInputChange}
                  className="form-control"
                  style={{ fontSize: '14px', height: '40px' }}
                />
              </div>

              {/* AR/CR Flag as Radio Buttons */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>AR / CR Flag</label>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="is_cr"
                      checked={editForm.is_cr === 0}
                      onChange={(e) => handleInputChange({ target: { name: 'is_cr', value: e.target.checked ? 0 : 1 } })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>AR (Debit)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="is_cr"
                      checked={editForm.is_cr === 1}
                      onChange={(e) => handleInputChange({ target: { name: 'is_cr', value: e.target.checked ? 1 : 0 } })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>CR (Credit)</span>
                  </label>
                </div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>Collection Summary</div>
              
              <div className="form-row" style={{ marginBottom: '20px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Amount Collected</label>
                  <input
                    type="text"
                    name="amount_collected"
                    value={editForm._display_amount_collected || ''}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/,/g, '');
                      const numValue = parseFloat(rawDigits);
                      setEditForm(prev => ({ 
                        ...prev, 
                        amount_collected: rawDigits === '' ? '0' : rawDigits,
                        _raw_amount_collected: isNaN(numValue) ? 0 : numValue,
                        _display_amount_collected: formatInputNumber(rawDigits)
                      }));
                    }}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const numValue = parseFloat(rawValue);
                      if (isNaN(numValue)) {
                        setEditForm(prev => ({ ...prev, amount_collected: '0.00', _raw_amount_collected: 0, _display_amount_collected: formatCurrency('0.00') }));
                      } else {
                        setEditForm(prev => ({ ...prev, amount_collected: numValue.toFixed(2), _raw_amount_collected: numValue, _display_amount_collected: formatCurrency(numValue) }));
                      }
                    }}
                    className="form-control"
                    placeholder="0.00"
                    style={{ fontSize: '15px', fontWeight: '600', textAlign: 'right' }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>PDC (Post-Dated Checks)</label>
                  <input
                    type="text"
                    name="pdc"
                    value={editForm._display_pdc || ''}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/,/g, '');
                      const numValue = parseFloat(rawDigits);
                      setEditForm(prev => ({ 
                        ...prev, 
                        pdc: rawDigits === '' ? '0' : rawDigits,
                        _raw_pdc: isNaN(numValue) ? 0 : numValue,
                        _display_pdc: formatInputNumber(rawDigits)
                      }));
                    }}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const numValue = parseFloat(rawValue);
                      if (isNaN(numValue)) {
                        setEditForm(prev => ({ ...prev, pdc: '0.00', _raw_pdc: 0, _display_pdc: formatCurrency('0.00') }));
                      } else {
                        setEditForm(prev => ({ ...prev, pdc: numValue.toFixed(2), _raw_pdc: numValue, _display_pdc: formatCurrency(numValue) }));
                      }
                    }}
                    className="form-control"
                    placeholder="0.00"
                    style={{ fontSize: '15px', fontWeight: '600', textAlign: 'right' }}
                  />
                </div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginTop: '24px', marginBottom: '12px' }}>Deductions & Balances</div>
              
              <div className="form-row" style={{ marginBottom: '20px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Discount Amount</label>
                  <input
                    type="text"
                    name="discount_amount"
                    value={editForm._display_discount || ''}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/,/g, '');
                      const numValue = parseFloat(rawDigits);
                      setEditForm(prev => ({ 
                        ...prev, 
                        discount_amount: rawDigits === '' ? '0' : rawDigits,
                        _raw_discount: isNaN(numValue) ? 0 : numValue,
                        _display_discount: formatInputNumber(rawDigits)
                      }));
                    }}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const numValue = parseFloat(rawValue);
                      if (isNaN(numValue)) {
                        setEditForm(prev => ({ ...prev, discount_amount: '0.00', _raw_discount: 0, _display_discount: formatCurrency('0.00') }));
                      } else {
                        setEditForm(prev => ({ ...prev, discount_amount: numValue.toFixed(2), _raw_discount: numValue, _display_discount: formatCurrency(numValue) }));
                      }
                    }}
                    className="form-control"
                    placeholder="0.00"
                    style={{ fontSize: '15px', fontWeight: '600', textAlign: 'right' }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Other Deductions</label>
                  <input
                    type="text"
                    name="other_deductions"
                    value={editForm._display_other_deductions || ''}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/,/g, '');
                      const numValue = parseFloat(rawDigits);
                      setEditForm(prev => ({ 
                        ...prev, 
                        other_deductions: rawDigits === '' ? '0' : rawDigits,
                        _raw_other_deductions: isNaN(numValue) ? 0 : numValue,
                        _display_other_deductions: formatInputNumber(rawDigits)
                      }));
                    }}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const numValue = parseFloat(rawValue);
                      if (isNaN(numValue)) {
                        setEditForm(prev => ({ ...prev, other_deductions: '0.00', _raw_other_deductions: 0, _display_other_deductions: formatCurrency('0.00') }));
                      } else {
                        setEditForm(prev => ({ ...prev, other_deductions: numValue.toFixed(2), _raw_other_deductions: numValue, _display_other_deductions: formatCurrency(numValue) }));
                      }
                    }}
                    className="form-control"
                    placeholder="0.00"
                    style={{ fontSize: '15px', fontWeight: '600', textAlign: 'right' }}
                  />
                </div>
              </div>

              <div className="form-row" style={{ marginBottom: '24px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Balance Collectible</label>
                  <input
                    type="text"
                    name="balance_collectibles"
                    value={editForm.balance_collectibles || '0'}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/,/g, '');
                      const numValue = parseFloat(rawDigits);
                      setEditForm(prev => ({ 
                        ...prev, 
                        balance_collectibles: rawDigits === '' ? '0' : rawDigits
                      }));
                    }}
                    className="form-control"
                    placeholder="0.00"
                    style={{ fontSize: '15px', backgroundColor: '#fffaf0', fontWeight: '700', color: '#c05621', border: '2px solid #fbd38d', textAlign: 'right' }}
                  />
                </div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginTop: '28px', marginBottom: '12px' }}>Additional Information</div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Remarks</label>
                <textarea
                  name="remarks"
                  value={editForm.remarks}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter remarks (optional)"
                  rows="4"
                  style={{ fontSize: '14px', paddingTop: '10px', paddingBottom: '10px', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', gap: '12px' }}>
              <button
                onClick={closeEditModal}
                className="btn btn-secondary"
                style={{ flex: 1, height: '42px', fontSize: '15px', borderColor: '#e63740', color: '#e63740' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEdit}
                className="btn btn-primary"
                disabled={saving}
                style={{ flex: 1, height: '42px', fontSize: '15px', fontWeight: '600', backgroundColor: '#e63740', borderColor: '#e63740' }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
